import { startCase, words } from 'lodash';

/**
 * Contentful i18n entry key whose `value` is a JSON object mapping lowercase
 * tokens to display strings, e.g. `{"sla":"SLA","ai":"AI","api":"API"}`.
 * Editors can add or adjust mappings without a code deploy.
 *
 * **Ambiguous short tokens (e.g. `os`):** Matching is per lodash `words()` token, so
 * `chaos` does not match `os`. CamelCase glue is different: `somethingOs` yields `Os`,
 * which would wrongly become `OS` if we always applied the glossary. We skip glossary
 * when the token is immediately preceded by a lowercase ASCII letter in the source
 * string (see `shouldApplyGlossaryAt`). Requiring spaces inside JSON keys does not fix
 * that case because normalizing to spaces still produces `something os`.
 */
export const DISPLAY_TOKEN_GLOSSARY_I18N_KEY = 'DISPLAY_TOKEN_GLOSSARY';

/** Lowercase ASCII letter: token is glued to a preceding camelCase segment. */
function isLowercaseAsciiLetter(char: string): boolean {
    return char >= 'a' && char <= 'z';
}

/**
 * Finds the next occurrence of `token` in `value` starting at `fromIndex` (for
 * matching lodash `words` order, including repeated tokens like `os_os`).
 */
function findNextTokenIndex(value: string, token: string, fromIndex: number): number {
    if (token.length === 0) {
        return -1;
    }
    const lowerValue = value.toLowerCase();
    const lowerToken = token.toLowerCase();
    const n = lowerToken.length;
    for (let i = Math.max(0, fromIndex); i <= value.length - n; i++) {
        if (lowerValue.slice(i, i + n) === lowerToken) {
            return i;
        }
    }
    return -1;
}

function shouldApplyGlossaryAt(value: string, tokenIndex: number): boolean {
    if (tokenIndex <= 0) {
        return true;
    }
    const prev = value.charAt(tokenIndex - 1);
    return !isLowercaseAsciiLetter(prev);
}

/**
 * Parses a glossary JSON string from the content service. Invalid or empty
 * input yields an empty map (callers then behave like `startCase` only).
 */
export function parseDisplayTokenGlossary(raw: string | null | undefined): Record<string, string> {
    if (raw === null || typeof raw !== 'string') {
        return {};
    }
    const trimmed = raw.trim();
    if (!trimmed) {
        return {};
    }
    try {
        const parsed: unknown = JSON.parse(trimmed);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }
        const out: Record<string, string> = {};
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
            if (typeof v === 'string') {
                out[k.toLowerCase()] = v;
            }
        }
        return out;
    } catch {
        return {};
    }
}

export function glossaryFromLookup(
    lookup: (key: string, defaultValue?: string) => string,
    glossaryI18nKey: string = DISPLAY_TOKEN_GLOSSARY_I18N_KEY,
): Record<string, string> {
    return parseDisplayTokenGlossary(lookup(glossaryI18nKey, '{}'));
}

/**
 * Splits the value the same way as `lodash/startCase` (via `words`), applies
 * glossary replacements per token (case-insensitive key match), then formats
 * any remaining tokens with `startCase` so behavior matches the prior pattern.
 */
export function formatDisplayLabel(
    value: string | null | undefined,
    glossary: Record<string, string> = {},
): string {
    if (value === undefined || value === null || value === '') {
        return '';
    }
    const ws = words(value);
    if (ws.length === 0) {
        return '';
    }
    let scanFrom = 0;
    return ws
        .map((word) => {
            const idx = findNextTokenIndex(value, word, scanFrom);
            if (idx >= 0) {
                scanFrom = idx + word.length;
            }
            const key = word.toLowerCase();
            const mapped = glossary[key];
            if (mapped !== undefined && idx >= 0 && shouldApplyGlossaryAt(value, idx)) {
                return mapped;
            }
            return startCase(word);
        })
        .join(' ');
}
